/**
 * DeliveryPointCard - Card de ponto de entrega
 * Exibe informacoes de uma entrega com acoes de edicao e remocao
 */

import { memo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  Scale as WeightIcon,
  PriorityHigh as PriorityIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

const DeliveryPointCard = memo(({
  delivery,
  index,
  onEdit,
  onRemove,
  isDragging = false,
  dragHandleProps = {},
}) => {
  const tokens = usePremiumTokens();

  const priorityColors = {
    high: tokens?.semantic?.error || '#f85149',
    medium: tokens?.semantic?.warning || '#d29922',
    low: tokens?.text?.secondary || '#8b949e',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        backgroundColor: isDragging
          ? `${tokens?.accent?.primary}15`
          : tokens?.surface?.raised,
        borderRadius: tokens?.radius?.md || '8px',
        border: `1px solid ${isDragging ? tokens?.accent?.primary : tokens?.border?.subtle}`,
        transition: tokens?.transitions?.all,
        '&:hover': {
          backgroundColor: tokens?.surface?.hover,
          borderColor: tokens?.border?.default,
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        {...dragHandleProps}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: tokens?.text?.tertiary,
          '&:hover': {
            color: tokens?.text?.secondary,
          },
        }}
      >
        <DragIcon fontSize="small" />
      </Box>

      {/* Numero da parada */}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: tokens?.accent?.primary,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: '12px',
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Box>

      {/* Informacoes */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: tokens?.text?.primary,
            fontWeight: 500,
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {delivery.name || `Entrega #${delivery.id}`}
        </Typography>

        <Typography
          sx={{
            color: tokens?.text?.secondary,
            fontSize: '12px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {delivery.address || `${delivery.lat?.toFixed(5)}, ${delivery.lng?.toFixed(5)}`}
        </Typography>

        {/* Tags */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
          {delivery.weight && (
            <Chip
              icon={<WeightIcon sx={{ fontSize: '12px !important' }} />}
              label={`${delivery.weight} kg`}
              size="small"
              sx={{
                height: 20,
                fontSize: '10px',
                backgroundColor: `${tokens?.accent?.secondary}20`,
                color: tokens?.accent?.secondary,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          )}

          {delivery.timeWindow && (
            <Chip
              icon={<TimeIcon sx={{ fontSize: '12px !important' }} />}
              label={`${delivery.timeWindow.start} - ${delivery.timeWindow.end}`}
              size="small"
              sx={{
                height: 20,
                fontSize: '10px',
                backgroundColor: `${tokens?.semantic?.info}20`,
                color: tokens?.semantic?.info,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          )}

          {delivery.priority === 'high' && (
            <Chip
              icon={<PriorityIcon sx={{ fontSize: '12px !important' }} />}
              label="Prioritario"
              size="small"
              sx={{
                height: 20,
                fontSize: '10px',
                backgroundColor: `${priorityColors.high}20`,
                color: priorityColors.high,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          )}
        </Box>
      </Box>

      {/* Acoes */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title="Editar">
          <IconButton
            size="small"
            onClick={() => onEdit?.(delivery)}
            sx={{
              color: tokens?.text?.tertiary,
              '&:hover': {
                color: tokens?.accent?.primary,
                backgroundColor: `${tokens?.accent?.primary}15`,
              },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Remover">
          <IconButton
            size="small"
            onClick={() => onRemove?.(delivery.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&:hover': {
                color: tokens?.semantic?.error,
                backgroundColor: `${tokens?.semantic?.error}15`,
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
});

DeliveryPointCard.displayName = 'DeliveryPointCard';

export default DeliveryPointCard;
